import React from 'react';
import { Tag, Tooltip } from 'antd';
import { FormattedMessage } from 'react-intl';
import {
    CloudSyncOutlined,
    CrownOutlined,
    UserAddOutlined,
    EditOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CrmLifecycleStage, ICrmPipelineStage } from '../../../services/nestjs-base-api/types';

dayjs.extend(utc);

// CRM reminder/task times are fixed AEST (UTC+10, no daylight saving) —
// SYR-95. Stored UTC; entered and displayed as AEST wall-clock regardless of
// the browser's timezone.
const AEST_OFFSET_HOURS = 10;

/** UTC instant → dayjs whose wall-clock fields read as AEST. */
export const aestFromUtc = (value: string | Date): Dayjs =>
    dayjs.utc(value).add(AEST_OFFSET_HOURS, 'hour');

/** Interpret a picker's wall-clock fields as AEST → UTC ISO string. */
export const utcFromAestWall = (wall: Dayjs): string =>
    dayjs
        .utc(wall.format('YYYY-MM-DD HH:mm:00'))
        .subtract(AEST_OFFSET_HOURS, 'hour')
        .toISOString();

export const formatAest = (value: string | Date): string =>
    aestFromUtc(value).format('YYYY-MM-DD h:mm A');

export const isPast = (value: string | Date): boolean =>
    new Date(value).getTime() <= Date.now();

export const lifecycleTagColor: Record<CrmLifecycleStage, string> = {
    prospect: 'default',
    registered: 'blue',
    signedIn: 'blue',
    onboardingComplete: 'cyan',
    surveyAccessed: 'geekblue',
    dashboardUnlocked: 'purple',
    fullTrialUnlocked: 'magenta',
    subscribed: 'green',
    churnRisk: 'orange',
    churned: 'red',
    foundingCohort: 'gold',
    founderLapsed: 'volcano',
};

export const LifecycleTag: React.FC<{ stage: CrmLifecycleStage }> = ({ stage }) => (
    <Tag color={lifecycleTagColor[stage] || 'default'} style={{ marginRight: 0 }}>
        <FormattedMessage id={`crm.lifecycle.${stage}`} defaultMessage={stage} />
    </Tag>
);

export const StageTag: React.FC<{ stage: ICrmPipelineStage }> = ({ stage }) => (
    <Tag color={stage.color} style={{ marginRight: 0 }}>
        {stage.label}
    </Tag>
);

export const SourceIcon: React.FC<{ source: 'sheet' | 'signup' | 'manual' | 'charter' }> = ({ source }) => {
    const icon =
        source === 'sheet' ? (
            <CloudSyncOutlined />
        ) : source === 'signup' ? (
            <UserAddOutlined />
        ) : source === 'charter' ? (
            <CrownOutlined />
        ) : (
            <EditOutlined />
        );
    return (
        <Tooltip title={<FormattedMessage id={`crm.source.${source}`} defaultMessage={source} />}>
            <span style={{ color: '#8c8c8c' }}>{icon}</span>
        </Tooltip>
    );
};

export const daysSince = (date: string | Date): number => {
    const ms = Date.now() - new Date(date).getTime();
    return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

const healthColor = (score: number): string =>
    score >= 70 ? '#52c41a' : score >= 40 ? '#faad14' : '#ff4d4f';

// Small health indicator for linked accounts (null score = not yet computed).
export const HealthDot: React.FC<{ score: number | null }> = ({ score }) => {
    if (score == null) return null;
    return (
        <Tooltip
            title={
                <FormattedMessage
                    id="crm.health.tooltip"
                    defaultMessage="Health score: {score}/100"
                    values={{ score }}
                />
            }
        >
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                    color: healthColor(score),
                    fontWeight: 600,
                }}
            >
                <span
                    style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: healthColor(score),
                    }}
                />
                {score}
            </span>
        </Tooltip>
    );
};
